<template>
  <div>
    <div v-if="mostrarBarraBotones" class="d-flex justify-content-end">
      <DxButton v-if="hiddenTypesList.length > 0" v-bind="defaultButtonProps($t('adjuntosOperacionCardList.hiddenTypesSelector.label'), true)"
        @click="showHiddenTypesModal" />
      <DxButton v-if="!withExternalUploadBtn" v-bind="secondaryButtonProps($t('adjuntosOperacionCardList.uploadDocuments.label'))" @click="uploadDocuments"
        icon="fa-solid fa-upload" />
    </div>
    <div class="cards-grid" :class="{ 'mt-4': mostrarBarraBotones }">
      <div v-for="grupo in sortedGruposAdjuntos" :key="grupo.key" :class="{ 'd-none': hiddenCards.has(grupo.key) }" @dragover.prevent>
        <DocumentCard ref="documentCards" v-bind="getDocumentCardProps(grupo)" @added:document="notificarCambioEstado" @updated:document="notificarCambioEstado"
          @deleted:document="notificarCambioEstado" @selected:document-type="notificarCambioEstado" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import DocumentCard from "@components/document-card/DocumentCard.vue";
import type { IDocumentInitialState, IDocumentCardProps } from "@components/document-card/models/IDocumentCardProps";
import { useIntl } from "@services/i18n/i18nservice";
import type { EstructuraDocumentalEmpresaAgentesModel, TipoDocumentoEmpresaAgentesEnum } from "@api/api";
import { ref, computed, onMounted, watch, nextTick } from "vue";
import type { CardFile } from "@components/document-card/models/CardFile";
import { useModalService } from "@services/modal-service";
import InputSelectBox, { type InputSelectBoxModalResult } from "@components/dialogs/InputSelectBox.vue";
import { messageBoxResult } from "@components/dialogs/message-box";
import { DxButton } from "devextreme-vue/button";
import { defaultButtonProps, secondaryButtonProps } from "@helpers/button-helper";

const emit = defineEmits<{
  "documents:upload": [];
  "state:changed": [];
}>();

const notificarCambioEstado = (): void => {
  void nextTick(() => {
    emit("state:changed");
  });
};

export interface AdjuntoEmpresaAgentes {
  Id: string;
  IdTipoDocumento?: string | undefined;
  Tipo: string | TipoDocumentoEmpresaAgentesEnum;
  Nombre: string;
  Ruta: string;
  Descripcion: string | null;
}

interface EstructuraDocumentalEmpresaAgentesRow {
  IdTipoDocumento?: string | undefined;
  TipoDocumento: string | TipoDocumentoEmpresaAgentesEnum;
  Requerido: boolean;
  Destacado: boolean;
  CodigoAgrupacion?: string | null | undefined;
  Plantilla?: string | null | undefined;
  RequiereDescripcion?: boolean;
}

const props = defineProps<{
  estructuraDocumental: EstructuraDocumentalEmpresaAgentesModel[];
  modoEdicion: "all" | "only-new-files" | "none";
  withExternalUploadBtn?: boolean;
  ocultarDocumentosNoDestacados?: boolean;
}>();

const ocultarDocumentosNoDestacados = computed(() => props.ocultarDocumentosNoDestacados !== false);

interface GrupoAdjuntos {
  key: string;
  tipos: EstructuraDocumentalEmpresaAgentesRow[];
  codigoAgrupacion: string | null;
  title: string;
}

const adjuntos = computed<AdjuntoEmpresaAgentes[]>(() =>
  props.estructuraDocumental
    .filter((e) => e.IdDocumentoAdjunto)
    .map((e) => ({
      Id: e.IdDocumentoAdjunto!,
      IdTipoDocumento: e.TipoDocumento,
      Tipo: e.TipoDocumento,
      Nombre: e.NombreAdjunto ?? "",
      Ruta: e.RutaAdjunto ?? "",
      Descripcion: null,
    })),
);

/** Una fila de configuración por tipo; el API puede devolver varias filas si hay varios adjuntos del mismo tipo. */
const configRows = computed<EstructuraDocumentalEmpresaAgentesRow[]>(() => {
  const porTipo = new Map<string, EstructuraDocumentalEmpresaAgentesRow>();
  for (const e of props.estructuraDocumental) {
    const key = String(e.TipoDocumento);
    const row: EstructuraDocumentalEmpresaAgentesRow = {
      IdTipoDocumento: e.TipoDocumento,
      TipoDocumento: e.TipoDocumento,
      Requerido: e.Requerido,
      Destacado: e.Destacado,
      CodigoAgrupacion: e.CodigoAgrupacion,
      Plantilla: undefined,
      RequiereDescripcion: false,
    };
    const existing = porTipo.get(key);
    if (!existing) {
      porTipo.set(key, row);
      continue;
    }
    existing.Requerido = existing.Requerido || row.Requerido;
    existing.Destacado = existing.Destacado || row.Destacado;
  }
  return Array.from(porTipo.values());
});

const intl = useIntl();
const documentCards = ref<InstanceType<typeof DocumentCard>[]>([]);
const hiddenCards = ref<Set<string>>(new Set());
const modal = useModalService();

const gruposAdjuntos = computed(() => {
  const gruposMap = new Map<string, GrupoAdjuntos>();

  for (const tipo of configRows.value) {
    const key = tipo.CodigoAgrupacion ?? tipo.TipoDocumento;

    if (!gruposMap.has(key)) {
      gruposMap.set(key, {
        key: key,
        tipos: [],
        codigoAgrupacion: tipo.CodigoAgrupacion ?? null,
        title: "",
      });
    }

    gruposMap.get(key)!.tipos.push(tipo);
  }

  for (const grupo of gruposMap.values()) {
    grupo.tipos.sort((a, b) => a.TipoDocumento.localeCompare(b.TipoDocumento));

    const baseKey = grupo.codigoAgrupacion ?? grupo.tipos[0].TipoDocumento;
    const i18nKey = `tipoDocumentoEmpresaAgentes.values.${baseKey}`;
    grupo.title = intl.i18n.te(i18nKey) ? intl.i18n.t(i18nKey) : String(grupo.tipos[0].TipoDocumento);
  }

  return Array.from(gruposMap.values());
});

const sortedGruposAdjuntos = computed(() => {
  return [...gruposAdjuntos.value].sort((a, b) => {
    return a.title.localeCompare(b.title);
  });
});

const hiddenTypesList = computed(() => {
  return Array.from(hiddenCards.value).map((key) => {
    const grupo = gruposAdjuntos.value.find((g) => g.key === key);
    if (grupo) {
      return {
        Id: key,
        Denominacion: grupo.title,
      };
    }
    return {
      Id: key,
      Denominacion: key,
    };
  });
});

const mostrarBarraBotones = computed(
  () => props.modoEdicion !== "none" && (hiddenTypesList.value.length > 0 || !props.withExternalUploadBtn),
);

const getAccionesAdjunto = (grupo: GrupoAdjuntos): ("open" | "download" | "delete" | "replace" | "add" | "copy")[] => {
  switch (props.modoEdicion) {
    case "all":
      return ["open", "download", "delete", "replace", "add", "copy"];
    case "only-new-files":
      return ["open", "download", "add"];
    case "none":
      return ["open", "download"];
  }
};

const getDocumentCardProps = (grupo: GrupoAdjuntos): IDocumentCardProps => {
  const tiposPorCodigo = new Map<string, { code: string; name: string }>();
  for (const tipo of grupo.tipos) {
    const code = String(tipo.IdTipoDocumento ?? tipo.TipoDocumento);
    if (!tiposPorCodigo.has(code)) {
      tiposPorCodigo.set(code, {
        code,
        name: intl.i18n.te(`tipoDocumentoEmpresaAgentes.values.${tipo.TipoDocumento}`)
          ? intl.i18n.t(`tipoDocumentoEmpresaAgentes.values.${tipo.TipoDocumento}`)
          : String(tipo.TipoDocumento),
      });
    }
  }
  const tiposDocumentos = Array.from(tiposPorCodigo.values());

  const requiereDescripcion = grupo.tipos.some((tipo) => tipo.RequiereDescripcion);
  const esRequerido = grupo.tipos.some((t) => t.Requerido);
  const templateUri = grupo.tipos.find((t) => t.Plantilla)?.Plantilla;

  const documentosIniciales = getInitialDocumentsForGrupo(grupo);

  return {
    title: grupo.title,
    allowedFileFormats: ["all"],
    initialDocumentList: documentosIniciales,
    actions: getAccionesAdjunto(grupo),
    maxFiles: 10,
    additionalDescriptionMode: requiereDescripcion ? "required" : "none",
    types: tiposDocumentos,
    required: props.modoEdicion === "none" ? false : esRequerido,
    templateUri: templateUri ?? undefined,
  } as IDocumentCardProps;
};

const getInitialDocumentsForGrupo = (grupo: GrupoAdjuntos): IDocumentInitialState[] => {
  const tiposEnGrupo = grupo.tipos.map((t) => String(t.IdTipoDocumento ?? t.TipoDocumento));
  return adjuntos.value
    .filter((a) => tiposEnGrupo.includes(String(a.IdTipoDocumento ?? a.Tipo)))
    .map(
      (a): IDocumentInitialState => ({
        id: a.Id,
        uri: a.Ruta,
        fileName: a.Nombre,
        description: a.Descripcion ?? undefined,
        typeCode: String(a.IdTipoDocumento ?? a.Tipo),
      }),
    );
};

const getFiles = (): CardFile[] => {
  const result: CardFile[] = [];

  for (let i = 0; i < sortedGruposAdjuntos.value.length; i++) {
    const documentCard = documentCards.value[i];

    if (documentCard) {
      const cardFiles = documentCard.getFiles();

      for (const fileId in cardFiles) {
        const file = cardFiles[fileId];
        result.push({
          id: file.id,
          file: file.file,
          fileName: file.fileName,
          remoteUri: file.remoteUri,
          uri: file.uri,
          status: file.status,
          description: file.description,
          typeCode: file.typeCode,
        });
      }
    }
  }

  return result;
};

const isValid = (): boolean => {
  let allValid = true;

  documentCards.value.forEach((card, i) => {
    const grupo = sortedGruposAdjuntos.value[i];
    if (!grupo) {
      return;
    }

    const estaOculto = hiddenCards.value.has(grupo.key);
    const esRequerido = grupo.tipos.some((t) => t.Requerido);

    if (estaOculto && !esRequerido) {
      return;
    }

    if (!card?.isValid) {
      allValid = false;
    }
  });

  return allValid;
};

const validarDocumentacion = (): boolean => {
  for (const grupo of gruposAdjuntos.value) {
    const esRequerido = grupo.tipos.some((t) => t.Requerido);
    if (esRequerido && hiddenCards.value.has(grupo.key)) {
      showCard(grupo.key);
    }
  }
  return isValid();
};

const removeCard = (key: string): void => {
  const index = sortedGruposAdjuntos.value.findIndex((grupo) => grupo.key === key);
  if (index !== -1 && documentCards.value[index]) {
    documentCards.value[index].removeAllFiles();
  }

  hiddenCards.value.add(key);
};

const showCard = (key: string): void => {
  hiddenCards.value.delete(key);
};

const hideNonHighlightedCards = (): void => {
  if (!ocultarDocumentosNoDestacados.value) {
    return;
  }
  for (const grupo of gruposAdjuntos.value) {
    const tiposEnGrupo = grupo.tipos.map((t) => String(t.TipoDocumento));
    const hasDocuments = adjuntos.value.some((a) => tiposEnGrupo.includes(String(a.Tipo)));
    const esDestacado = grupo.tipos.some((t) => t.Destacado);

    if (props.modoEdicion === "none") {
      if (!hasDocuments) {
        hiddenCards.value.add(grupo.key);
      }
    } else {
      if (!hasDocuments && !esDestacado) {
        hiddenCards.value.add(grupo.key);
      }
    }
  }
};

const showHiddenTypesModal = async (): Promise<void> => {
  if (hiddenTypesList.value.length === 0) {
    return;
  }

  const result = (await modal.showModal(
    InputSelectBox,
    {
      label: intl.i18n.t("adjuntosOperacionCardList.hiddenTypesSelector.label"),
      required: true,
      dataSource: hiddenTypesList.value,
    },
    {
      title: intl.i18n.t("adjuntosOperacionCardList.hiddenTypesSelector.title"),
      width: 500,
      height: 260,
    },
  )) as InputSelectBoxModalResult;

  if (result && result.result === messageBoxResult.ok && result.value) {
    const selectedKey = result.value as string;
    showCard(selectedKey);
    notificarCambioEstado();
  }
};

const uploadDocuments = (): void => {
  emit("documents:upload");
};

const resetAll = (): void => {
  hiddenCards.value = new Set();
  for (let i = 0; i < sortedGruposAdjuntos.value.length; i++) {
    const grupo = sortedGruposAdjuntos.value[i];
    documentCards.value[i]?.resetToInitial(getInitialDocumentsForGrupo(grupo));
  }

  hideNonHighlightedCards();
  notificarCambioEstado();
};

watch(
  () => props.estructuraDocumental,
  () => {
    resetAll();
  },
  { deep: true },
);

onMounted(() => {
  hideNonHighlightedCards();
  notificarCambioEstado();
});

defineExpose({
  getFiles,
  isValid,
  validarDocumentacion,
  removeCard,
  showCard,
  resetAll,
});
</script>

<style scoped lang="scss">
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  width: 100%;

  >div {
    display: contents;
  }
}

.document-card {
  width: 100%;
  min-height: 150px;
  background: var(--background-normal-default);
  border: 1px solid var(--border-default-default);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-upload);
  padding-bottom: 16px;
}

.document-card-header .title,
.document-card .card-header h3 {
  min-height: 46px;
  padding: 24px 8px 4px;
  color: var(--text-default-caption);
  font-family: var(--font-family-headings);
  font-size: var(--font-size-heading-h5);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-heading-h5);
  text-align: center;
}
</style>
